/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import ReactSelect from 'react-select'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { physicianFormalName } from 'helpers/nameHelper'
import { TableWrapper } from 'components/atoms/TableWrapper'
import Button from 'components/atoms/Button'
import DataTable from 'components/atoms/DataTable'
import { confirm } from 'components/molecules/Confirmation'
import {
  useAddPhysicianFollowUpPhysicianMutation,
  useGetEffectiveFollowUpPhysiciansQuery,
  useGetFollowUpPhysiciansQuery,
  useGetPhysicianFollowUpPhysiciansQuery,
  useRemovePhysicianFollowUpPhysicianMutation,
  useSetDefaultFollowUpPhysicianMutation,
  useUpdatePhysicianFollowUpCareNoneMutation,
} from 'store/services/labPhysician'
import { isOrganization } from '../../../helpers/followUpProvidersHelper'
import routes from '../constants/routes'

interface IProps {
  physicianNpi: string
  defaultFollowUpPhysicianId?: number
  onDefaultChanged: () => void
}

type SelectOption = {
  value: string
  label: string
}

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.4rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

export const FollowUpProvidersSection = ({
  physicianNpi,
  defaultFollowUpPhysicianId,
  onDefaultChanged,
}: IProps) => {
  const navigate = useNavigate()
  const [selectedProvider, setSelectedProvider] = useState<SelectOption | null>(null)

  const {
    data: ownFollowUpPhysicians,
    isLoading,
    refetch,
  } = useGetPhysicianFollowUpPhysiciansQuery({ physicianNpi })

  const { data: effective } = useGetEffectiveFollowUpPhysiciansQuery({ physicianNpi })

  const { data: availableProviders, isLoading: isLoadingProviders } = useGetFollowUpPhysiciansQuery(
    {},
  )

  const [addFollowUpPhysician, { isLoading: isAdding }] = useAddPhysicianFollowUpPhysicianMutation()
  const [removeFollowUpPhysician] = useRemovePhysicianFollowUpPhysicianMutation()
  const [setDefaultFollowUpPhysician] = useSetDefaultFollowUpPhysicianMutation()
  const [updateFollowUpCareNone, { isLoading: isUpdatingNone }] =
    useUpdatePhysicianFollowUpCareNoneMutation()

  // When the physician has no own providers, settings are inherited from the group (read-only).
  const isInherited = effective?.source === 'group'
  const followUpPhysicians = isInherited ? effective?.items : ownFollowUpPhysicians
  const effectiveDefaultId = isInherited
    ? effective?.defaultFollowUpPhysicianId ?? undefined
    : defaultFollowUpPhysicianId
  const isFollowUpCareNone = effective?.noneExplicitlySet === true && effective.source === 'physician'
  const isFollowUpCareNoneInherited = effective?.noneExplicitlySet === true && effective.source === 'group'

  // Filter out already assigned physicians
  const availableOptions = useMemo(() => {
    if (!availableProviders) return []
    const assignedNpis = followUpPhysicians?.map((p) => p.followUpPhysician?.npi) || []

    return availableProviders
      .filter((p) => !assignedNpis.includes(p.npi))
      .map((p) => ({
        value: p.npi,
        label: isOrganization(p.providerType)
          ? `${p.organizationName} - NPI: ${p.npi}`
          : `${physicianFormalName(p.firstName, p.lastName, p.credential)} - NPI: ${p.npi}`,
      }))
  }, [availableProviders, followUpPhysicians])

  const handleAddProvider = async () => {
    if (!selectedProvider) return

    try {
      await addFollowUpPhysician({
        physicianNpi,
        followUpPhysicianNpi: selectedProvider.value,
      }).unwrap()
      setSelectedProvider(null)
      refetch()
    } catch (error) {
      console.error('Error adding follow-up physician:', error)
    }
  }

  const handleRemoveProvider = async (
    followUpPhysicianNpi: string,
    followUpPhysicianId: number,
  ) => {
    confirm('', {
      yesAction: 'Yes',
      description: 'Are you sure you want to remove this follow-up physician assignment?',
    })
      .then(async () => {
        try {
          await removeFollowUpPhysician({ physicianNpi, followUpPhysicianNpi }).unwrap()
          // If we're removing the default, clear the default
          if (followUpPhysicianId === defaultFollowUpPhysicianId) {
            await setDefaultFollowUpPhysician({
              physicianNpi,
              defaultFollowUpPhysicianNpi: null,
            }).unwrap()
            onDefaultChanged()
          }
          refetch()
        } catch (error) {
          console.error('Error removing follow-up physician:', error)
        }
      })
      .catch(() => {})
  }

  const handleSetDefault = async (followUpPhysicianNpi: string) => {
    try {
      await setDefaultFollowUpPhysician({
        physicianNpi,
        defaultFollowUpPhysicianNpi: followUpPhysicianNpi,
      }).unwrap()
      onDefaultChanged()
    } catch (error) {
      console.error('Error setting default follow-up physician:', error)
    }
  }

  const handleClearDefault = async () => {
    try {
      await setDefaultFollowUpPhysician({
        physicianNpi,
        defaultFollowUpPhysicianNpi: null,
      }).unwrap()
      onDefaultChanged()
    } catch (error) {
      console.error('Error clearing default follow-up physician:', error)
    }
  }

  const handleToggleFollowUpCareNone = async (checked: boolean) => {
    try {
      await updateFollowUpCareNone({ physicianNpi, isNone: checked }).unwrap()
    } catch (error) {
      console.error('Error updating follow-up care none flag:', error)
      toast.error('Failed to update follow-up care setting.')
    }
  }

  const tableColumns = [
    {
      accessor: 'providerType',
      Header: 'Provider Type',
      disableSortBy: true,
      Cell: ({ row }) => {
        const followUpPhysician = row.original.followUpPhysician
        return (
          <div>
            {isOrganization(followUpPhysician?.providerType) ? 'Organization' : 'Individual'}
          </div>
        )
      },
    },
    {
      accessor: 'name',
      Header: 'Name',
      disableSortBy: true,
      Cell: ({ row }) => {
        const followUpPhysician = row.original?.followUpPhysician
        const label = isOrganization(followUpPhysician.providerType)
          ? followUpPhysician.organizationName
          : physicianFormalName(
              followUpPhysician.firstName,
              followUpPhysician.lastName,
              followUpPhysician.credential,
            )
        return (
          <Button
            css={{ all: 'unset' }}
            label={label}
            onClick={() =>
              navigate(
                `${routes.index}${routes.physician.root}${routes.physician.upsert}${row.original?.followUpPhysician.npi}`,
              )
            }
          />
        )
      },
    },
    {
      accessor: 'npi',
      Header: 'NPI',
      disableSortBy: true,
      Cell: ({ row }) => <div>{row.original.followUpPhysician?.npi}</div>,
    },
    {
      accessor: 'isDefault',
      Header: 'Default',
      disableSortBy: true,
      Cell: ({ row }) => {
        // Convert to numbers for comparison to handle string/number type mismatch
        const isDefault =
          effectiveDefaultId !== undefined &&
          Number(row.original.followUpPhysicianId) === Number(effectiveDefaultId)

        if (isInherited) {
          return (
            <div>
              <i
                className={isDefault ? 'fa fa-star text-warning' : 'far fa-star text-muted'}
                css={{ fontSize: '1.2rem' }}
              />
            </div>
          )
        }

        return (
          <div>
            {isDefault ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={handleClearDefault}
                title="Click to unset as default">
                <i className="fa fa-star text-warning" css={{ fontSize: '1.2rem' }} />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => handleSetDefault(row.original.followUpPhysician.npi)}
                title="Set as default">
                <i className="far fa-star text-muted" css={{ fontSize: '1.2rem' }} />
              </button>
            )}
          </div>
        )
      },
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) =>
        isInherited ? null : (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() =>
              handleRemoveProvider(
                row.original.followUpPhysician.npi,
                row.original.followUpPhysicianId,
              )
            }>
            Remove
          </button>
        ),
    },
  ]

  return (
    <div className="card card-primary mt-3">
      <div className="row p-3">
        <div className="col-sm-12">
          <h4 className="mb-3">Follow-up Care Providers</h4>
        </div>

        {isInherited && (
          <div className="col-sm-12">
            <div className="alert alert-info py-2" css={{ fontSize: 13 }}>
              Inherited from group. Adding a provider here creates a physician-specific override that
              replaces the group's follow-up care providers list.
            </div>
          </div>
        )}

        <div className="col-sm-12 mb-2">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="followUpCareNone"
              checked={isFollowUpCareNone}
              disabled={isInherited || isFollowUpCareNoneInherited || isUpdatingNone}
              onChange={(e) => handleToggleFollowUpCareNone(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="followUpCareNone">
              No follow-up care
            </label>
          </div>
          {isFollowUpCareNoneInherited && (
            <small className="text-muted d-block mt-1">
              No follow-up care (inherited from group).
            </small>
          )}
        </div>

        {!isFollowUpCareNone && !isFollowUpCareNoneInherited && (
          <>
            <div className="col-sm-12 mb-3">
              <div className="row align-items-end">
                <div className="col-sm-8">
                  <label className="form-label">Add Follow-up Care Provider</label>
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    isLoading={isLoadingProviders}
                    placeholder="Search for a follow-up physician..."
                    value={selectedProvider}
                    onChange={(option) => setSelectedProvider(option as SelectOption | null)}
                    options={availableOptions}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
                  />
                </div>
                <div className="col-sm-4">
                  <Button
                    className="btn btn-primary"
                    label="Add Physician"
                    icon="fa fa-plus"
                    disabled={!selectedProvider || isAdding}
                    onClick={handleAddProvider}
                  />
                </div>
              </div>
            </div>

            <div className="col-sm-12">
              <StyledTableWrapper className="card-body row">
                <DataTable
                  tableKey={'physician_follow_up_physicians'}
                  data={{ items: followUpPhysicians || [] }}
                  status={isLoading}
                  tableColumns={tableColumns}
                  pageSize={10}
                  entityName="follow-up physician"
                />
              </StyledTableWrapper>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
